import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PublicNav } from "@/components/PublicNav";
import { PublicFooter } from "@/components/PublicFooter";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: "About — VISIKT" },
      {
        name: "description",
        content:
          "VISIKT is built by VIKEEP AS in Oslo, Norway. Track subscriptions, salary growth, and inflation-adjusted purchasing power.",
      },
      { property: "og:title", content: "About — VISIKT" },
      {
        property: "og:description",
        content:
          "VISIKT is built by VIKEEP AS in Oslo, Norway. Track subscriptions, salary growth, and real purchasing power.",
      },
      { property: "og:url", content: "https://visikt.com/about" },
    ],
    links: [{ rel: "canonical", href: "https://visikt.com/about" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: "VIKEEP AS",
          alternateName: "VISIKT",
          url: "https://visikt.com",
          email: "contact@vikeep.no",
          taxID: "938 008 272",
          vatID: "NO938008272",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Oslo",
            addressCountry: "NO",
          },
        }),
      },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="max-w-2xl mx-auto w-full px-5 pt-8 flex items-center justify-between">
        <Link to="/" className="font-display font-extrabold tracking-tighter text-xl">
          VISIKT
        </Link>
        <PublicNav />
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full px-5 pt-14 pb-16 space-y-10">
        <section className="animate-entry">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-4">
            {t("about.eyebrow")}
          </p>
          <h1 className="text-4xl font-display font-bold tracking-tight leading-[1.05]">
            {t("about.headline")}
          </h1>
          <p className="mt-5 text-base text-muted-foreground leading-relaxed">{t("about.intro")}</p>
        </section>

        <section className="animate-entry [animation-delay:60ms] space-y-3">
          <h2 className="text-lg font-display font-semibold tracking-tight">
            {t("about.whatSectionTitle")}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">{t("about.whatBody")}</p>
        </section>

        <section className="animate-entry [animation-delay:120ms] space-y-4">
          <h2 className="text-lg font-display font-semibold tracking-tight">
            {t("about.companySectionTitle")}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed">{t("about.companyBody")}</p>
          <dl className="grid grid-cols-1 sm:grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm border border-border rounded-lg p-5 bg-card">
            <dt className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground self-center">
              {t("about.orgNoLabel")}
            </dt>
            <dd className="text-foreground">938 008 272</dd>

            <dt className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground self-center">
              {t("about.addressLabel")}
            </dt>
            <dd className="text-foreground">{t("about.addressValue")}</dd>

            <dt className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground self-center">
              {t("about.contactLabel")}
            </dt>
            <dd>
              <a
                href="mailto:contact@vikeep.no"
                className="text-foreground hover:opacity-70 underline underline-offset-2"
              >
                contact@vikeep.no
              </a>
            </dd>

            <dt className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground self-center">
              {t("about.parentSiteLabel")}
            </dt>
            <dd>
              <a
                href="https://www.vikeep.no"
                target="_blank"
                rel="noopener noreferrer"
                className="text-foreground hover:opacity-70 underline underline-offset-2"
              >
                {t("about.parentSiteValue")}
              </a>
            </dd>
          </dl>
        </section>

        <PublicFooter />
      </main>
    </div>
  );
}
